import { useEffect, useState } from "react";
import type { GraphyClient } from "./api/client";
import { ApiError } from "./api/client";
import type { NodeRef, SnapshotRef } from "./api/types";
import { Overview } from "./overview/Overview";
import { SystemMap } from "./map/SystemMap";
import { Explorer } from "./explorer/Explorer";
import { McpExplorer } from "./mcp/McpExplorer";
import { Contributors } from "./contributors/Contributors";
import { Wiki } from "./wiki/Wiki";
import { Reviews } from "./reviews/Reviews";
import { ErrorState, Loading } from "./components/states";
import { SourceDrawer } from "./components/SourceDrawer";
import { NotificationCenter } from "./components/NotificationCenter";
import { Icon, type IconName } from "./components/icons";
import vhMark from "./assets/visionhammer-mark.svg";
import { useViewState } from "./state/useViewState";
import { useLiveUpdates } from "./state/useLiveUpdates";
import { T } from "./i18n";

type TabKey = "overview" | "map" | "explore" | "tools" | "contributors" | "wiki" | "reviews";
const TABS: { key: TabKey; label: string; icon: IconName }[] = [
  { key: "overview", label: T.app.overview, icon: "overview" },
  { key: "map", label: T.app.map, icon: "map" },
  { key: "explore", label: T.app.explore, icon: "explore" },
  { key: "tools", label: T.app.tools, icon: "tools" },
  { key: "contributors", label: T.app.contributors, icon: "contributors" },
  { key: "wiki", label: T.app.wiki, icon: "wiki" },
  { key: "reviews", label: T.app.reviews, icon: "reviews" },
];

// Shell for the two E08 surfaces. Cross-view context lives in the URL (GY-035):
// back/reload and copied links restore the same authorized view, or explain a
// stale reference and fall back to a meaningful default. The project is a
// selector only — authority is resolved server-side from the session.

export function App({ client, projectId }: { client: GraphyClient; projectId: string }) {
  const [state, patch] = useViewState();
  const [snapshot, setSnapshot] = useState<SnapshotRef | null>(null);
  const [nodes, setNodes] = useState<NodeRef[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [stale, setStale] = useState<string | null>(null);
  const [sourceSymbol, setSourceSymbol] = useState<string | null>(null);
  const live = useLiveUpdates(client, projectId);
  const [overviewSeen, setOverviewSeen] = useState(0);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(false);
    (async () => {
      try {
        const snap = await client.snapshot({ projectId });
        if (!live) return;
        setSnapshot(snap);
        if (snap) {
          const ns = await client.nodes({ projectId, snapshotId: snap.snapshotId });
          if (!live) return;
          setNodes(ns);
          reconcile(snap, ns);
        }
      } catch (e) {
        if (e instanceof ApiError || e instanceof Error) setError(true);
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, projectId]);

  // Reconcile the URL-restored context against the authorized snapshot (GY-035).
  function reconcile(snap: SnapshotRef, ns: NodeRef[]) {
    const ids = new Set(ns.map((n) => n.id));
    const firstId = ns[0]?.id ?? "";
    if (state.snapshotId && state.snapshotId !== snap.snapshotId) {
      // the link was pinned to a different snapshot — re-pin and explain
      setStale(T.common.staleReference);
      patch({ snapshotId: snap.snapshotId, seed: firstId, selected: null, affected: false, scenarioId: null });
      return;
    }
    const patchObj: Record<string, unknown> = {};
    if (!state.snapshotId) patchObj.snapshotId = snap.snapshotId;
    if (!state.seed || !ids.has(state.seed)) {
      if (state.seed && !ids.has(state.seed)) setStale(T.common.staleSymbol);
      patchObj.seed = firstId;
    }
    if (state.selected && !ids.has(state.selected)) {
      setStale(T.common.staleSymbol);
      patchObj.selected = null;
    }
    if (state.target && !ids.has(state.target)) patchObj.target = firstId;
    if (Object.keys(patchObj).length) patch(patchObj);
  }

  const drillDown = (id: string) => patch({ seed: id, tab: "explore" });
  const nodeLabel = (id: string) => nodes.find((n) => n.id === id)?.label ?? id;

  return (
    <div className="app">
      <header className="app-header">
        <div className="brand">
          <img className="brand-mark-img" src={vhMark} alt="VisionHammer" width={26} height={26} />
          <h1>VisionHammer <span className="brand-sub">Graphy</span></h1>
        </div>
        <nav className="app-tabs" role="tablist" aria-label={T.app.title}>
          {TABS.map(({ key, label, icon }) => {
            const C = Icon[icon];
            return (
              <button
                key={key}
                role="tab"
                aria-selected={state.tab === key}
                className={state.tab === key ? "selected-tab" : ""}
                onClick={() => patch({ tab: key })}
              >
                <C size={15} />
                {label}
              </button>
            );
          })}
        </nav>
        <div className="app-header-right">
          {snapshot && (
            <span className="coverage-pill" title={snapshot.repos.join(", ")}>
              <span className="dot" aria-hidden="true" />
              <b>{snapshot.symbolCount}</b> symbols
              <span className="muted">· {snapshot.repos.length} {snapshot.repos.length === 1 ? "repo" : "repos"}</span>
            </span>
          )}
          <NotificationCenter
            feed={live.feed}
            unread={live.unread}
            markAllRead={live.markAllRead}
            onSelect={() => patch({ tab: "overview" })}
          />
        </div>
      </header>
      {stale && (
        <div className="banner banner-partial app-stale" role="status">
          <span>{stale}</span>
          <button className="btn btn-ghost" onClick={() => setStale(null)}>
            {T.common.dismiss}
          </button>
        </div>
      )}
      <main className="app-main">
        {error ? (
          <ErrorState />
        ) : loading ? (
          <Loading />
        ) : state.tab === "overview" ? (
          <Overview
            client={client}
            projectId={projectId}
            onNavigate={(tab) => patch({ tab })}
            pendingCount={Math.max(0, live.feed.length - overviewSeen)}
            onRefreshed={() => setOverviewSeen(live.feed.length)}
          />
        ) : state.tab === "map" ? (
          <SystemMap client={client} projectId={projectId} snapshot={snapshot} nodes={nodes} state={state} patch={patch} onDrillDown={drillDown} onViewSource={setSourceSymbol} />
        ) : state.tab === "explore" ? (
          <Explorer client={client} projectId={projectId} snapshotId={snapshot?.snapshotId ?? ""} nodes={nodes} state={state} patch={patch} onViewSource={setSourceSymbol} />
        ) : state.tab === "tools" ? (
          <McpExplorer client={client} scope={{ projectId, snapshotId: snapshot?.snapshotId ?? "" }} labelOf={nodeLabel} onViewSource={setSourceSymbol} />
        ) : state.tab === "contributors" ? (
          <Contributors client={client} projectId={projectId} onExploreCommunity={(cid) => patch({ tab: "explore", mode: "communities", community: String(cid) })} />
        ) : state.tab === "wiki" ? (
          <Wiki
            client={client}
            projectId={projectId}
            onViewSource={setSourceSymbol}
            onExploreCommunity={(cid) => patch({ tab: "explore", mode: "communities", community: String(cid) })}
          />
        ) : (
          <Reviews
            client={client}
            projectId={projectId}
            onViewSource={setSourceSymbol}
            onExploreSymbol={(id) => patch({ tab: "explore", mode: "neighborhood", seed: id })}
          />
        )}
      </main>
      {sourceSymbol && snapshot && (
        <SourceDrawer
          client={client}
          scope={{ projectId, snapshotId: snapshot.snapshotId }}
          symbolId={sourceSymbol}
          labelOf={nodeLabel}
          onClose={() => setSourceSymbol(null)}
          onNavigate={(id) => {
            setSourceSymbol(null);
            patch({ seed: id, tab: "explore" });
          }}
        />
      )}
    </div>
  );
}
